import { ConfigProvider, ThemeConfig } from 'antd';
import React, { ReactElement } from 'react';
import { observer } from 'mobx-react';
import { CssVariables } from 'styles/cssVariables';
import ReactDOM, { Root } from 'react-dom/client';
import ReactDOMOld from 'react-dom';

export type AntConfigProviderProps = {
	children: React.ReactNode;
	theme?: Partial<ThemeConfig>;
};

export const AntConfigProvider = observer((props: AntConfigProviderProps) => {
	const theme: ThemeConfig = {
		token: {
			fontFamily: 'Oxygen, Arial, sans-serif',
			fontSize: 12,
			lineHeight: 1.5,
			colorError: CssVariables.severityCritical,
			colorSuccess: CssVariables.severityNone,
			colorWarning: CssVariables.severityMajor,
			zIndexPopupBase: CssVariables.currentZIndex,
		},
		components: {
			Tabs: {
				horizontalMargin: '0 0 6px 0',
			},
			Input: {
				paddingBlock: 6,
			},
		},
		cssVar: { key: 'css-var-r0' },
		hashed: false,
	};

	if (props.theme) {
		const { token, ...others } = props.theme;
		theme.token = Object.assign(theme.token, token);
		Object.assign(theme, others);
	}

	// antd 6 changed Tag's default variant from bordered to "filled" (transparent border) app-wide -
	// restore the old bordered look here instead of passing variant="outlined" at every call site
	return (
		<ConfigProvider theme={theme} tag={{ variant: 'outlined' }}>
			{props.children}
		</ConfigProvider>
	);
});

export function wrapWithAntdConfigProvider(Component: ReactElement) {
	return <AntConfigProvider>{Component}</AntConfigProvider>;
}

export function patchReactWithAntContext() {
	// react 18+
	const originalCreateRoot = ReactDOM.createRoot;

	ReactDOM.createRoot = (container: Element | DocumentFragment): Root => {
		const root = originalCreateRoot(container);
		// eslint-disable-next-line @typescript-eslint/unbound-method -- called via .call(root, ...) below, so `this` is bound at the call site
		const originalRender = root.render;

		root.render = (element: ReactElement) => {
			originalRender.call(root, wrapWithAntdConfigProvider(element));
		};
		return root;
	};

	// react 17-
	const originalRender = ReactDOMOld.render;

	// @ts-expect-error - react-dom's legacy `render` typing doesn't allow reassignment
	ReactDOMOld.render = (element: ReactElement, container: Element | DocumentFragment, callback?: () => void): void => {
		// Wrap the element with ConfigProvider
		originalRender(wrapWithAntdConfigProvider(element), container, callback);
	};
}
